import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function RegisterPage() {
    const { register } = useAuth();
    const nav = useNavigate();
    const { t } = useTranslation();
    const [form, setForm] = useState({
        name: "",
        service_name: "",
        email: "",
        phone: "",
        address: "",
        nipt: "",
        password: "",
    });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        const res = await register({
            name: form.name,
            email: form.email,
            password: form.password,
            service_name: form.service_name,
            phone: form.phone,
            address: form.address || null,
            nipt: form.nipt || null,
        });
        setLoading(false);
        if (res.ok) nav("/pending");
        else setErr(res.error);
    };

    return (
        <div className="min-h-[calc(100vh-120px)] grid md:grid-cols-2" data-testid="register-page">
            <div className="flex items-center justify-center p-8 order-2 md:order-1">
                <div className="w-full max-w-md">
                    <div className="label-kicker mb-2">{t("auth.register_kicker")}</div>
                    <h1 className="font-display font-black text-3xl uppercase mb-2">{t("auth.request_access")}.</h1>
                    <p className="text-sm text-slate-600 mb-6">
                        {t("auth.approved_subtitle")}
                    </p>

                    <form onSubmit={onSubmit} className="space-y-3">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label-kicker mb-1 block">{t("auth.full_name")} *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={update("name")}
                                    required
                                    className="input-field"
                                    data-testid="register-name-input"
                                />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">{t("auth.service_name")} *</label>
                                <input
                                    type="text"
                                    value={form.service_name}
                                    onChange={update("service_name")}
                                    required
                                    placeholder="Servisi Cimi"
                                    className="input-field"
                                    data-testid="register-service-name-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="label-kicker mb-1 block">{t("auth.email")} *</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                required
                                className="input-field"
                                data-testid="register-email-input"
                            />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <label className="label-kicker mb-1 block">{t("auth.phone")} *</label>
                                <input
                                    type="tel"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    required
                                    placeholder="+355 69 ..."
                                    className="input-field"
                                    data-testid="register-phone-input"
                                />
                            </div>
                            <div>
                                <label className="label-kicker mb-1 block">{t("auth.password")} * (min 6)</label>
                                <input
                                    type="password"
                                    value={form.password}
                                    onChange={update("password")}
                                    required
                                    minLength={6}
                                    className="input-field"
                                    data-testid="register-password-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="label-kicker mb-1 block">{t("auth.address")} <span className="text-slate-400 normal-case">({t("auth.optional")})</span></label>
                            <input
                                type="text"
                                value={form.address}
                                onChange={update("address")}
                                className="input-field"
                                data-testid="register-address-input"
                            />
                        </div>
                        <div>
                            <label className="label-kicker mb-1 block">{t("auth.nipt")} <span className="text-slate-400 normal-case">({t("auth.optional")})</span></label>
                            <input
                                type="text"
                                value={form.nipt}
                                onChange={update("nipt")}
                                placeholder="L12345678X"
                                className="input-field"
                                data-testid="register-nipt-input"
                            />
                        </div>

                        {err && (
                            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="register-error">
                                <AlertCircle className="w-4 h-4" />
                                <span>{err}</span>
                            </div>
                        )}
                        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="register-submit">
                            <UserPlus className="w-4 h-4" /> {loading ? t("auth.creating") : t("auth.request_button")}
                        </button>
                    </form>

                    <p className="mt-6 text-sm text-slate-600">
                        {t("auth.have_account")}{" "}
                        <Link to="/login" className="text-[hsl(var(--primary))] font-semibold">
                            {t("auth.log_in")}
                        </Link>
                    </p>
                </div>
            </div>

            <div className="hidden md:block relative overflow-hidden bg-slate-900 order-1 md:order-2">
                <div className="absolute inset-0 grid-bg opacity-20" />
                <div className="relative p-12 h-full flex flex-col justify-between text-white">
                    <div>
                        <div className="w-9 h-9 bg-[hsl(var(--primary))] flex items-center justify-center text-white font-display font-black">
                            TC
                        </div>
                        <div className="label-kicker text-slate-400 mt-6">New Account · Pending approval</div>
                    </div>
                    <div>
                        <h2 className="font-display font-black text-4xl uppercase leading-none">
                            Gated<br />
                            <span className="text-[hsl(var(--primary))]">dealer</span><br />
                            access.
                        </h2>
                        <ul className="mt-6 space-y-2 font-mono text-sm text-slate-400">
                            <li>1 → You register</li>
                            <li>2 → Admin reviews &amp; approves</li>
                            <li>3 → You browse catalog &amp; prices</li>
                            <li>4 → Add items to basket</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
